import type {
    ComponentStageData,
    MobileAlgoPlugin,
    MobileAlgoPluginInitializationArgs,
    PluginContext,
    PluginHeuristicsRegistry,
    PluginStageHandler
} from '../../types'
import {widthHeuristics, horizontalMarginsHeuristics, getAvailableWidthKey} from './heuristicsDefinitions'
import {findHeuristic, getParent, createAdaptiveHeuristic} from '../common'
import type {Pointer, UnitSize} from '@wix/document-services-types'
import {getTargetLayoutPointer} from '../../conversionDal/utils/pointer'
import type {LayoutEntryItem} from '../../conversionDal/types'
import type {HorizontalScaleAndSizePluginAPI} from './types'
import {SemanticTypes} from '../../preprocess/conversionDataBuilders'

export const createPlugin = ({stages}: MobileAlgoPluginInitializationArgs): MobileAlgoPlugin => {
    const updateAvailableWidthByExistingLayout = (
        {componentPointer, conversionDal, config}: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const availableWidthKey = getAvailableWidthKey(componentPointer)

        const parentPointer = getParent(conversionDal, componentPointer)
        const isBoxPlugin = conversionDal.get(componentPointer).semanticType === SemanticTypes.BOX_PLUGIN

        if (!parentPointer) {
            return contextHelper!.set(availableWidthKey, config.targetCanvasSize)
        }

        const availableWidth = isBoxPlugin
            ? config.targetCanvasSize
            : contextHelper!.get(getAvailableWidthKey(parentPointer))

        const currentWidth =
            (
                (conversionDal.get(getTargetLayoutPointer(componentPointer)) as LayoutEntryItem).componentLayout
                    ?.width as UnitSize
            )?.value ?? 100

        contextHelper!.set(availableWidthKey, (currentWidth / 100) * availableWidth)
    }

    const run: PluginStageHandler<ComponentStageData> = (
        componentStageData: ComponentStageData,
        contextHelper?: PluginContext
    ) => {
        const marginsHeuristicToApply = findHeuristic(horizontalMarginsHeuristics, componentStageData)
        marginsHeuristicToApply.apply(componentStageData, contextHelper)
        const widthHeuristicToApply = findHeuristic(widthHeuristics, componentStageData)
        widthHeuristicToApply.apply(componentStageData, contextHelper)

        return {
            appliedHeuristics: [marginsHeuristicToApply.heuristicCode, widthHeuristicToApply.heuristicCode]
        }
    }

    const register = (registry: PluginHeuristicsRegistry) => {
        registry.register(
            stages.HORIZONTAL_LAYOUT_SCALE,
            createAdaptiveHeuristic(run, updateAvailableWidthByExistingLayout)
        )
    }

    const createApi = (context: PluginContext): HorizontalScaleAndSizePluginAPI => {
        return {
            getWidth: (compPointer: Pointer): number => {
                return context.get(getAvailableWidthKey(compPointer))
            }
        }
    }

    const name = 'horizontalScaleAndSize'

    return {register, createApi, name, dependencies: []}
}
